/**
 * ExamPatientValidationAlert — Displays patient ↔ CPT exam validation warnings.
 *
 * Shows validation issues with appropriate severity styling:
 * - CRITICAL: Red alert with error icon, blocks actions
 * - WARNING:  Amber alert with warning icon
 * - INFO:     Blue informational chip
 *
 * Placed in CurrentExaminationPanel between CPT selector and study fields.
 */
import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  CircularProgress,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { ValidationIssue } from '../services/validationService';
import { typography } from '~/theme';

interface ExamPatientValidationAlertProps {
  /** List of validation issues to display */
  issues: ValidationIssue[];
  /** Whether validation is currently running */
  loading?: boolean;
  /** Whether the validation result blocks actions (has critical issues) */
  blocked?: boolean;
}

/** Map severity → MUI Alert severity */
const severityMap = {
  critical: 'error' as const,
  warning: 'warning' as const,
  info: 'info' as const,
};

/** Map severity → display label */
const severityLabel = {
  critical: 'Critical',
  warning: 'Warning',
  info: 'Info',
};

const ExamPatientValidationAlert: React.FC<ExamPatientValidationAlertProps> = ({
  issues,
  loading = false,
  blocked = false,
}) => {
  // Nothing to show
  if (!loading && issues.length === 0) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          Validating patient–exam compatibility…
        </Typography>
      </Box>
    );
  }

  // Group issues by severity for display ordering (critical first)
  const critical = issues.filter((i) => i.severity === 'critical');
  const warnings = issues.filter((i) => i.severity === 'warning');
  const infos = issues.filter((i) => i.severity === 'info');
  const ordered = [...critical, ...warnings, ...infos];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {ordered.map((issue, idx) => (
        <Alert
          key={`${issue.rule_id}-${idx}`}
          severity={severityMap[issue.severity] || 'info'}
          variant="outlined"
          icon={
            issue.severity === 'critical' ? (
              <ErrorOutlineIcon fontSize="small" />
            ) : issue.severity === 'warning' ? (
              <WarningAmberIcon fontSize="small" />
            ) : (
              <CheckCircleOutlineIcon fontSize="small" />
            )
          }
          sx={{
            py: 0.25,
            px: 1,
            '& .MuiAlert-message': { py: 0.25 },
            '& .MuiAlert-icon': { py: 0.5, mr: 0.75 },
            borderRadius: 1.5,
            fontSize: typography.caption.fontSize,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              label={severityLabel[issue.severity] || 'Info'}
              size="small"
              color={
                issue.severity === 'critical'
                  ? 'error'
                  : issue.severity === 'warning'
                  ? 'warning'
                  : 'info'
              }
              variant="filled"
              sx={{ height: 18, fontSize: typography.overline.fontSize, fontWeight: typography.h1.fontWeight }}
            />
            <Typography variant="caption" fontWeight={typography.label.fontWeight} sx={{ lineHeight: typography.h3.lineHeight }}>
              {issue.message}
            </Typography>
          </Box>
          <Collapse in>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, lineHeight: typography.caption.lineHeight, fontSize: typography.caption.fontSize }}
            >
              {issue.details}
            </Typography>
          </Collapse>
        </Alert>
      ))}

      {/* Blocked indicator */}
      {blocked && (
        <Alert severity="error" variant="filled" sx={{ py: 0.25, px: 1, borderRadius: 1.5 }}>
          <Typography variant="caption" fontWeight={typography.h1.fontWeight}>
            Dictation and report generation are blocked until the above issues are resolved.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ExamPatientValidationAlert;
